import type { ReactNode } from 'react';
import clsx from 'clsx';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

/**
 * Immer eine klassische `<table>`, auch auf dem Smartphone - eine gestapelte
 * Karten-Liste wirkt auf kleinen Displays uneinheitlich und erschwert den
 * Zeilen-zu-Zeile-Vergleich, den Wiegungslisten & Co. brauchen. Stattdessen
 * `overflow-x-auto` + `whitespace-nowrap` (bei Bedarf seitlich scrollen,
 * genau wie im Altsystem) und eine minimal kleinere Schrift unterhalb `md`,
 * damit trotzdem mehr Spalten ohne Scrollen sichtbar bleiben.
 */
export function DataTable<T>({ columns, rows, rowKey, emptyMessage = 'Keine Daten vorhanden.', onRowClick }: DataTableProps<T>) {
  if (rows.length === 0) return <EmptyState title={emptyMessage} />;

  return (
    <div className="overflow-x-auto rounded-md">
      <table className="w-full text-left text-sm max-md:text-[13.5px]">
        <thead>
          <tr className="bg-accent-500/15 text-xs font-semibold uppercase tracking-wide text-accent-300 max-md:text-[11px]">
            {columns.map((col) => (
              <th key={col.key} className={clsx('whitespace-nowrap px-3 py-2.5', col.className)}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={clsx('border-b border-surface-700', onRowClick && 'cursor-pointer hover:bg-accent-500/5')}
            >
              {columns.map((col) => (
                <td key={col.key} className={clsx('whitespace-nowrap px-3 py-2.5 align-middle', col.className)}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 pt-4 text-sm text-ivory-300">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="rounded-md border border-surface-600 px-3 py-1 hover:bg-surface-700 disabled:opacity-40"
      >
        Zurück
      </button>
      <span className="text-xs text-ivory-500">
        Seite {page} / {totalPages}
      </span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className="rounded-md border border-surface-600 px-3 py-1 hover:bg-surface-700 disabled:opacity-40"
      >
        Weiter
      </button>
    </div>
  );
}
